import React, { memo, useCallback, useMemo, useState } from 'react';
import { useStoreon } from 'storeon/react';
import { css } from 'linaria';
import { SelectionGroup, SelectionGroupOption, SelectionGroupValue } from 'common/components';
import { SectionTitle } from 'common/components/section-title';
import { Typography } from 'common/components/typography';
import { FETCH_PRODUCTS, PRODUCTS_STORE_NAME, selectActualProductList } from 'modules/products';
import { analyticsUtils } from 'utils/analytics-utils';
import { Solution, SolutionCard } from '../solution-card';
import { enhanceSolution } from './solution-section-utils';

const tabs = css`
  && {
    margin: 0;
    margin-bottom: 32px;

    @media (--only-mobile) {
      margin-bottom: 24px;
    }
  }
`;

const section = css`
  width: 100%;
  margin-bottom: 80px;

  @media (--only-mobile) {
    margin-bottom: 56px;
  }
`;

const solutionName = css`
  margin-bottom: 12px;
`;

const solutionText = css`
  && {
    width: 880px;
    margin-bottom: 24px;
    color: var(--text-secondary);

    @media (--only-tablet) {
      width: 545px;
    }

    @media (--only-mobile) {
      width: 100%;
      margin-bottom: 16px;
      font-size: 16px;
      line-height: 22px;
    }
  }
`;

type KidsSolutionProps = {
  content: Record<string, Solution>;
  reverse?: boolean;
  sectionName: string;
  selectionGroupOptionList: SelectionGroupOption[];
  title: string;
};

/**
 * Вью секции с решением для дома
 */
export const SolutionSection: React.FC<KidsSolutionProps> = memo(
  ({ content, reverse, sectionName, selectionGroupOptionList, title }) => {
    const [activeSelectionOption, setActiveSelectionOption] = useState(selectionGroupOptionList[0].value);
    const { products, dispatch } = useStoreon(PRODUCTS_STORE_NAME);

    const productList = useMemo(
      () => selectActualProductList(products, content[activeSelectionOption].productIds),
      [products, content, activeSelectionOption]
    );
    const solution = useMemo(() => enhanceSolution(productList, content[activeSelectionOption]), [
      productList,
      content,
      activeSelectionOption,
    ]);
    const loading = useMemo(() => products.loadingStatus?.[sectionName] === 'Pending', [
      products.loadingStatus,
      sectionName,
    ]);

    /**
     * Обработчик изменения таба
     */
    const handleTabChange = useCallback(
      /* @ts-expect-error: param typecheck error */
      (_, value: SelectionGroupValue) => {
        if (value !== activeSelectionOption) {
          setActiveSelectionOption(value as string);
          dispatch(FETCH_PRODUCTS.init, { productIdList: content[value as string].productIds, sectionName });

          const activeOption: SelectionGroupOption | undefined = selectionGroupOptionList.find(
            (option: SelectionGroupOption) => option.value === value
          );

          if (activeOption) {
            analyticsUtils.clickButtonEvent('landingSpaceoKub', activeOption.text, title);
          }
        }
      },
      [
        setActiveSelectionOption,
        selectionGroupOptionList,
        activeSelectionOption,
        content,
        sectionName,
        title,
        dispatch,
      ]
    );

    return (
      <section className={section}>
        <SectionTitle className={solutionName}>{title}</SectionTitle>
        <Typography variant="m" size="body_short" className={solutionText}>
          На&nbsp;фотографии представлен демонстрационный образец. Цена указана за&nbsp; полный комплект, для
          проверки наличия товаров добавьте комплект в&nbsp;корзину.
        </Typography>

        <SelectionGroup
          active={activeSelectionOption}
          className={tabs}
          onChange={handleTabChange}
          optionList={selectionGroupOptionList}
          variant="outline"
        />

        <SolutionCard
          cardName={title}
          loading={loading}
          productList={productList}
          reverse={reverse}
          solution={solution}
        />
      </section>
    );
  }
);
